import { useCallback, useContext, useEffect, useState } from "react";

import { Box, Button, Divider } from "@mui/material";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { enqueueSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { UsersApiHelperForMember } from "../../api/ApiHelper";
import { FarmVaultIcon } from "../Icons/Icons";
import { DocumentSelectDialog } from "./DocumentSelectDialog";
import EquipmentRepairCard from "./EquipmentRepairCard";
import EquipmentRepairDataTable from "./EquipmentRepairDataTable";
import { DOCUMENT_FORM_NAME_RECEIPT_EQUIPMENT } from "./FinancialHelpers";

const CARD_GRID_KEY = "equipment_inventory_repairs";

export default function EquipmentInventoryRepairsSection({ equipmentId, onRepairCreated, onEditRepair }) {
  const { globalState } = useContext(AppContext);
  const [workspaceMode, setWorkspaceMode] = useState(WORKSPACE_VIEW_TABLE);
  const [repairs, setRepairs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentSelectDialogOpen, setIsDocumentSelectDialogOpen] = useState(false);

  useEffect(() => {
    if (onRepairCreated?.id) {
      setRepairs((prev) => {
        const exists = prev.some((r) => r.id === onRepairCreated.id);
        if (exists) {
          return prev.map((r) => (r.id === onRepairCreated.id ? onRepairCreated : r));
        }
        return [...prev, onRepairCreated];
      });
    }
  }, [onRepairCreated]);

  const loadRepairs = useCallback(async () => {
    if (!globalState?.producer_token || !equipmentId) {
      setRepairs([]);
      return;
    }

    try {
      setIsLoading(true);
      const filter = {
        producer_token: globalState.producer_token,
        equipment_inventory_id: equipmentId,
      };

      const repairsData = await UsersApiHelperForMember.callPost(
        "financial/EquipmentInventoryRepair/search",
        filter,
        globalState
      );
      setRepairs(repairsData || []);
    } catch (err) {
      console.warn("Error loading repairs:", err);
      enqueueSnackbar(err.detail || err.message || "Error loading repairs", {
        variant: "error",
      });
      setRepairs([]);
    } finally {
      setIsLoading(false);
    }
  }, [equipmentId, globalState?.producer_token]);

  useEffect(() => {
    loadRepairs();
  }, [loadRepairs]);

  const handleEdit = (repair) => {
    if (onEditRepair) {
      onEditRepair(repair);
    }
  };

  const handleDelete = async (repair) => {
    if (!globalState?.producer_token || !repair?.id) {
      enqueueSnackbar("Unable to delete repair: missing required information", {
        variant: "error",
      });
      return;
    }

    try {
      await UsersApiHelperForMember.callPost(
        "financial/EquipmentInventoryRepair",
        {
          EquipmentInventoryRepair: [{ ...repair, is_active: false }],
          producer_token: globalState.producer_token,
        },
        globalState
      );

      await loadRepairs();
    } catch (err) {
      console.warn("Error deleting repair:", err);
      enqueueSnackbar(err.detail || err.message || "Error deleting repair", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  };

  const handleDocumentSelectClose = async (documentContents) => {
    setIsDocumentSelectDialogOpen(false);

    if (documentContents) {
      // Transform the imported data to match API format
      if (documentContents.EquipmentInventoryRepair && Array.isArray(documentContents.EquipmentInventoryRepair)) {
        try {
          const repairsToImport = documentContents.EquipmentInventoryRepair.map((repair) => ({
            ...repair,
            id: uuidv4(),
            equipment_inventory_id: equipmentId,
          }));

          // Make API call to save the imported repairs
          await UsersApiHelperForMember.callPost(
            "financial/EquipmentInventoryRepair",
            {
              EquipmentInventoryRepair: repairsToImport,
              producer_token: globalState.producer_token,
            },
            globalState
          );

          // Reload the repairs list to show the imported data
          await loadRepairs();
        } catch (err) {
          console.warn("Error importing repairs:", err);
          enqueueSnackbar(err.detail || err.message || "Error importing repairs", {
            variant: "error",
          });
        }
      }
    }
  };

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <WorkspacePageHeader
        title="Repairs"
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
        additionalControls={
          <Button variant="outlined" startIcon={<FarmVaultIcon />} onClick={() => setIsDocumentSelectDialogOpen(true)}>
            Import from Farm Vault
          </Button>
        }
      />
      {/*Import*/}
      <DocumentSelectDialog
        open={isDocumentSelectDialogOpen}
        onClose={handleDocumentSelectClose}
        document_type_name={DOCUMENT_FORM_NAME_RECEIPT_EQUIPMENT}
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {repairs.map((repair) => (
            <EquipmentRepairCard key={repair.id} repair={repair} onEdit={handleEdit} onDelete={handleDelete} />
          ))}

          {!repairs?.length && !isLoading && (
            <EmptyCard text="No repairs" secondaryText="Get started by adding a repair" isLoading={isLoading} />
          )}
        </Box>
      )}
      {workspaceMode === WORKSPACE_VIEW_TABLE && (
        <EquipmentRepairDataTable repairs={repairs} onEdit={handleEdit} onDelete={handleDelete} isLoading={isLoading} />
      )}
    </>
  );
}
